/**
 * Client-side security helpers. These do not replace server-side checks.
 */

export function isInternalPath(path) {
  if (typeof path !== 'string' || !path) return false
  if (!path.startsWith('/')) return false
  if (path.startsWith('//') || path.startsWith('/\\')) return false
  if (path.includes('://')) return false
  return true
}

export function isInternalRoute(route) {
  return !!(route && isInternalPath(route.fullPath || route.path))
}

export function isSafeOAuthParam(value) {
  return typeof value === 'string'
    && value.length > 0
    && value.length < 4096
    && !/[\s<>"'\\]/.test(value)
}

export function sanitizeOAuthParam(value) {
  return isSafeOAuthParam(value) ? value : undefined
}

export function escapeHtml(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

export function escapeHtmlAttr(value) {
  return escapeHtml(value)
}

export function setSafeText(el, value) {
  if (!el) return
  el.textContent = value == null ? '' : String(value)
}

export function isSafeHttpUrl(value) {
  if (typeof value !== 'string' || !value) return false
  try {
    const url = new URL(value)
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch (e) {
    return false
  }
}

/** http(s) URL or same-app path. Rejects javascript:/data: and protocol-relative URLs. */
export function safeHttpUrl(value) {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  if (!trimmed || trimmed.length > 4096) return null
  if (isInternalPath(trimmed)) return trimmed
  try {
    const withScheme = /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(trimmed)
      ? trimmed
      : `https://${trimmed}`
    const url = new URL(withScheme)
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null
    return url.href
  } catch (e) {
    return null
  }
}

export function safeReturnUrl(raw, fallback) {
  if (typeof raw !== 'string' || !raw) return fallback
  if (isInternalPath(raw)) return raw
  try {
    const url = new URL(raw, typeof window !== 'undefined' ? window.location.origin : undefined)
    if (typeof window !== 'undefined' && url.origin === window.location.origin) {
      const path = `${url.pathname}${url.search}${url.hash}`
      if (isInternalPath(path) || isInternalPath(url.pathname)) return path
    }
  } catch (e) {
    // ignore
  }
  return fallback
}

export function safePageUrl() {
  try {
    const url = new URL(window.location.href)
    ;['token', 'secret', 'code', 'session_state', 'state', 'isLogged'].forEach(key => {
      url.searchParams.delete(key)
    })
    return url.toString()
  } catch (e) {
    return window.location.pathname || '/'
  }
}

const UNSAFE_HTML_TAGS = ['script', 'iframe', 'object', 'embed', 'link', 'meta', 'style', 'form', 'base']

export function sanitizeHtml(html) {
  if (html == null || html === '') return ''
  if (typeof window === 'undefined' || !window.document) return ''
  const template = document.createElement('template')
  template.innerHTML = String(html)
  const walk = node => {
    Array.from(node.childNodes).forEach(child => {
      if (child.nodeType !== 1) return
      const tag = child.tagName.toLowerCase()
      if (UNSAFE_HTML_TAGS.includes(tag)) {
        child.remove()
        return
      }
      Array.from(child.attributes).forEach(attr => {
        const name = attr.name.toLowerCase()
        const val = String(attr.value || '')
        if (name.startsWith('on') || name === 'srcdoc') {
          child.removeAttribute(attr.name)
          return
        }
        if ((name === 'href' || name === 'src' || name === 'xlink:href') && /^\s*javascript:/i.test(val)) {
          child.removeAttribute(attr.name)
        }
      })
      walk(child)
    })
  }
  walk(template.content)
  return template.innerHTML
}

const DANGEROUS_INNER_EXTENSIONS = [
  'php', 'phtml', 'phar', 'exe', 'js', 'mjs', 'html', 'htm', 'svg',
  'shtml', 'cgi', 'asp', 'aspx', 'jsp', 'sh', 'bat', 'cmd', 'com', 'dll', 'htaccess',
]

export function hasDangerousUploadFilename(name) {
  if (typeof name !== 'string' || !name) return true
  const base = name.split(/[/\\]/).pop()
  if (!base || base.includes('\0')) return true
  const parts = base.split('.')
  if (parts.length > 2) {
    const inner = parts.slice(0, -1).map(part => part.toLowerCase())
    if (inner.some(part => DANGEROUS_INNER_EXTENSIONS.includes(part))) return true
  }
  return false
}

export function cookieSetOptions() {
  return {
    sameSite: 'Lax',
    secure: typeof window !== 'undefined' && window.location.protocol === 'https:',
  }
}
